'use client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faFileExcel,
  faFileCsv,
  faXmark,
  faCloudUpload,
  faCheck,
  faTimes
} from '@fortawesome/pro-regular-svg-icons';
import Button from 'src/components/era-package/forms/Button';
import Buttons from 'src/components/era-package/era/Buttons';


const FileUploadModal = ({
  open,
  onClose,
  onFileSelect,
  acceptedFileTypes = ['.xlsx', '.xls', '.csv'],
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  title = 'Upload File',
  description = 'Select or drag and drop your file to upload',
  children,
  formData,
  setFormData,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setError('');
      setIsDragOver(false);
      setIsUploading(false);
    }
  }, [open]);

  // File validation
  const validateFile = useCallback((file) => {
    if (!file) return 'No file selected';

    // Check file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!acceptedFileTypes.includes(fileExtension)) {
      return `Invalid file type. Accepted types: ${acceptedFileTypes.join(', ')}`;
    }

    // Check file size
    if (file.size > maxFileSize) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
      return `File size exceeds ${maxSizeMB}MB limit`;
    }

    return null;
  }, [acceptedFileTypes, maxFileSize]);

  // Handle file selection
  const handleFileSelect = useCallback((file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    setError('');
    setSelectedFile(file);
  }, [validateFile]);

  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle click to select file
  const handleClickToSelect = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Handle file input change
  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  }, [handleFileSelect]);

  // Handle upload
  const handleUpload = useCallback(() => {
    if (selectedFile && onFileSelect) {
      setIsUploading(true);

      // Log the formData to console as requested
      console.log('FileUploadModal formData:', formData);
    
      // Pass both file and formData to parent
      onFileSelect(selectedFile, formData);

      // Close modal after a brief delay to allow parent to process
      setTimeout(() => {
        onClose();
      }, 100);
    }
  }, [selectedFile, onFileSelect, onClose, formData]);

  // Handle close
  const handleClose = useCallback(() => {
    if (!isUploading) {
      onClose();
    }
  }, [isUploading, onClose]);

  // Remove selected file
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setError('');
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Get file icon
  const getFileIcon = useCallback((fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'xlsx':
      case 'xls':
        return faFileExcel;
      case 'csv':
        return faFileCsv;
      default:
        return faUpload;
    }
  }, []);


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          maxWidth: '100dvw',
          width: '512px',
          maxHeight: {
            xs: '100dvh',
            sm: '90vh',
          },
          m: {
            xs: 0,
            sm: 4,
          },
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {title}
          <IconButton
            aria-label="Close upload data file modal"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
            title="Close modal"
          >
            <Box component={FontAwesomeIcon} icon={faTimes} sx={{ width: '1em' }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: {
            xs: 2,
            sm: 4,
          },
        }}
      >
        <Box py={1} textAlign="center">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>

          {/* Drop Zone */}
          <Box
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleClickToSelect}
            sx={{
              borderRadius: 2,
              border: `2px dashed ${error
                ? theme.palette.error.main
                : isDragOver
                  ? theme.palette.primary.main
                  : theme.palette.divider
                }`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
              cursor: 'pointer',
              backgroundColor: isDragOver
                ? alpha(theme.palette.primary.main, 0.04)
                : error
                  ? alpha(theme.palette.error.main, 0.04)
                  : alpha(theme.palette.action.hover, 0.02),
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <FontAwesomeIcon
              icon={faCloudUpload}
              size="3x"
              style={{
                color: error
                  ? theme.palette.error.main
                  : isDragOver
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
              }}
            />

            <Box>
              <Typography variant="h6" color="primary">
                {isDragOver ? 'Drop your file here' : 'Drag & drop your file here'}
              </Typography>

              <Typography variant="body2" color="primary">
                or click to browse files
              </Typography>
            </Box>

            <Box>
              <Chip
                label={`Accepted: ${acceptedFileTypes.join(', ')}`}
                size="small"
                variant="outlined"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Maximum file size: {formatFileSize(maxFileSize)}
              </Typography>
            </Box>
          </Box>

          {children && (
            <Box width="100%" textAlign="left" mt={3}>
              {children}
            </Box>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Selected File Preview */}
          {selectedFile && !error && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.success.main, 0.04),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FontAwesomeIcon
                  icon={getFileIcon(selectedFile.name)}
                  size="2x"
                  style={{ color: theme.palette.success.main }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Processing file...
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 3 }}>
        <Buttons
          advance={
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || error || isUploading}
              startIcon={<FontAwesomeIcon icon={faCheck} />}
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          }
          other={
            <Button onClick={handleClose} disabled={isUploading}>
              Cancel
            </Button>
          }
        />
      </DialogActions>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept={acceptedFileTypes.join(',')}
        style={{ display: 'none' }}
      />
    </Dialog>
  );
};

export default FileUploadModal;
